import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client.ts";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import {
  Download, Filter, RefreshCw, ExternalLink, Users, BookOpen,
  Activity, Microscope, ChevronDown, ChevronUp, ArrowUpRight,
} from "lucide-react";

// ─── Constants ────────────────────────────────────────────────────────────────

const COST_URL = "https://www.cost.eu/actions/CA24123/";
const NEUROX_URL = "https://neuroxrehab.com/";

const REVIEW_META: Record<string, { icon: React.ReactNode; tagline: string; description: string }> = {
  ASSESSMENT: {
    icon: <Microscope className="h-5 w-5" />,
    tagline: "Assessment & Diagnosis",
    description:
      "Studies evaluating XR-based tools for detecting, measuring, and monitoring spatial neglect severity following acquired brain injury.",
  },
  TREATMENT: {
    icon: <Activity className="h-5 w-5" />,
    tagline: "Rehabilitation & Treatment",
    description:
      "Clinical trials and intervention studies using head-mounted XR devices to rehabilitate spatial neglect and improve functional outcomes.",
  },
  TECHNICAL: {
    icon: <BookOpen className="h-5 w-5" />,
    tagline: "Technology & Development",
    description:
      "Technical design, validation, and feasibility of XR hardware and software systems purpose-built for neurorehabilitation.",
  },
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface FilterPreset {
  id: string;
  name: string;
  description: string | null;
  filters: Record<string, unknown>;
}

interface PublicStudy {
  id: string;
  title: string;
  authors: string[];
  year: number | null;
  journal: string | null;
  doi: string | null;
  pmid: string | null;
  source: string;
  status: string;
  reviews: { id: string; name: string; type: string }[];
  xrModality: string | null;
  deviceName: string | null;
  deviceManufacturer: string | null;
  sampleSize: number | null;
  studyDesign: string | null;
  participantType: string | null;
  diagnoses: string[] | null;
  country: string | null;
  extractionStatus: "confirmed" | "preliminary" | "none";
}

interface PrismaFlow {
  identified: number;
  duplicatesRemoved: number;
  screened: number;
  excludedAbstract: number;
  fullTextAssessed: number;
  excludedFullText: number;
  included: number;
  lastUpdated: string;
}

interface ReviewStat {
  review: { id: string; name: string; type: string };
  screened: number;
  included: number;
  extracted: number;
}

interface AuthorStats {
  authors: { name: string; count: number }[];
  totalAuthors: number;
  yearMin: number | null;
  yearMax: number | null;
}

interface StudiesResponse {
  studies: PublicStudy[];
  pagination: { total: number; page: number; totalPages: number };
  lastUpdated: string;
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function PublicViewPage() {
  const [selectedPreset, setSelectedPreset] = useState<FilterPreset | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showPrisma, setShowPrisma] = useState(false);
  const [showCloud, setShowCloud] = useState(true);

  const { data: presetsData } = useQuery({
    queryKey: ["filter-presets"],
    queryFn: () => api.get<{ presets: FilterPreset[] }>("/public/filter-presets"),
  });

  const { data: prismaData } = useQuery({
    queryKey: ["prisma-flow"],
    queryFn: () => api.get<PrismaFlow>("/public/prisma-flow"),
  });

  const { data: reviewStats } = useQuery({
    queryKey: ["public-review-stats"],
    queryFn: () => api.get<{ stats: ReviewStat[]; lastUpdated: string }>("/public/stats"),
  });

  const { data: authorStats } = useQuery({
    queryKey: ["author-stats"],
    queryFn: () => api.get<AuthorStats>("/public/author-stats"),
  });

  const filters = selectedPreset?.filters ?? {};
  const params = new URLSearchParams({
    page: String(page),
    limit: "25",
    ...(search ? { search } : {}),
    ...(filters["reviewType"] ? { reviewType: String(filters["reviewType"]) } : {}),
  });

  const { data: studiesData, isLoading, isFetching } = useQuery({
    queryKey: ["public-studies", params.toString()],
    queryFn: () => api.get<StudiesResponse>(`/public/studies?${params.toString()}`),
  });

  const handleExport = (type: "csv" | "bibtex") => {
    const url = `/api/public/export/${type}`;
    window.open(url, "_blank");
  };

  const lastUpdated = studiesData?.lastUpdated
    ? new Date(studiesData.lastUpdated).toLocaleDateString("en-GB", {
        day: "numeric", month: "long", year: "numeric",
      })
    : null;

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* ── Header ── */}
      <header className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-4">
            <img src="/neuroxrehab-logo.svg" alt="neuroXRehab" className="h-10 w-auto" />
            <div>
              <p className="text-xs font-semibold tracking-wider uppercase text-brand-magenta leading-tight">
                Living Systematic Review Database
              </p>
              <p className="text-xs text-muted-foreground leading-tight">
                XR Neurorehabilitation · Spatial Neglect
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <a
              href={COST_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
            >
              COST Action CA24123
              <ExternalLink className="h-3 w-3" />
            </a>
            <a
              href={NEUROX_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-brand-magenta text-white text-xs font-medium hover:opacity-90 transition-opacity"
            >
              Get involved
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <div className="bg-gradient-to-b from-primary/5 to-background border-b">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="max-w-3xl">
            <h1 className="text-2xl md:text-3xl font-bold mb-3 leading-snug">
              Extended Reality Neurorehabilitation<br className="hidden md:block" /> of Spatial Neglect
            </h1>
            <p className="text-muted-foreground leading-relaxed mb-2">
              This database is produced by{" "}
              <a href={NEUROX_URL} target="_blank" rel="noopener noreferrer" className="text-foreground underline underline-offset-2 hover:text-primary">
                neuroXRehab
              </a>
              , COST Action{" "}
              <a href={COST_URL} target="_blank" rel="noopener noreferrer" className="text-foreground underline underline-offset-2 hover:text-primary">
                CA24123
              </a>
              . It is a continuously updated, living systematic review of all published research on
              head-mounted extended reality (XR) devices used for the assessment and rehabilitation
              of spatial neglect and related disorders after acquired brain injury.
            </p>
            <p className="text-sm text-muted-foreground">
              Records are screened and extracted by a multi-centre team of researchers across Europe.
              All data is freely available for download. The database is updated as new studies are published.
            </p>
          </div>

          {/* Stats strip */}
          {prismaData && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              <StatPill
                value={prismaData.included}
                label="Included studies"
                highlight
              />
              <StatPill
                value={prismaData.identified}
                label="Records identified"
              />
              {authorStats?.totalAuthors ? (
                <StatPill value={authorStats.totalAuthors} label="Unique authors" />
              ) : (
                <StatPill value={prismaData.duplicatesRemoved} label="Duplicates removed" />
              )}
              {authorStats?.yearMin && authorStats?.yearMax ? (
                <StatPill
                  value={`${authorStats.yearMin}–${authorStats.yearMax}`}
                  label="Publication years"
                />
              ) : (
                <StatPill value={3} label="Active reviews" />
              )}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-10">

        {/* ── Review type cards ── */}
        {reviewStats?.stats && reviewStats.stats.length > 0 && (
          <section>
            <h2 className="text-base font-semibold mb-4">What we review</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {reviewStats.stats.map((s) => {
                const meta = REVIEW_META[s.review.type] ?? {
                  icon: <BookOpen className="h-5 w-5" />,
                  tagline: s.review.type,
                  description: s.review.name,
                };
                return (
                  <div key={s.review.id} className="border rounded-xl p-5 bg-card">
                    <div className="flex items-center gap-2.5 mb-3 text-primary">
                      {meta.icon}
                      <span className="font-semibold text-sm text-foreground">{meta.tagline}</span>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                      {meta.description}
                    </p>
                    <div className="grid grid-cols-3 gap-2 pt-3 border-t text-center text-xs text-muted-foreground">
                      <div>
                        <p className="font-bold text-base text-foreground">{s.screened}</p>
                        <p>Screened</p>
                      </div>
                      <div>
                        <p className="font-bold text-base text-brand-magenta">{s.included}</p>
                        <p>Included</p>
                      </div>
                      <div>
                        <p className="font-bold text-base text-foreground">{s.extracted}</p>
                        <p>Extracted</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Author word cloud ── */}
        {authorStats && (authorStats.authors.length > 0 || !authorStats) && (
          <section className="border rounded-xl bg-card overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-accent/30 transition-colors"
              onClick={() => setShowCloud((v) => !v)}
            >
              <div className="flex items-center gap-2.5">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="font-semibold text-sm">Key researchers in this field</span>
                {authorStats.totalAuthors > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {authorStats.totalAuthors} authors across {prismaData?.included ?? "-"} included studies
                  </span>
                )}
              </div>
              {showCloud
                ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </button>

            {showCloud && (
              <div className="border-t px-5 py-5">
                {authorStats.authors.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    Author data will appear here once studies are included in the review.
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground mb-4">
                      Author size reflects number of included publications. Hover for details.
                    </p>
                    <AuthorCloud authors={authorStats.authors} onSearch={(name) => { setSearch(name); setPage(1); }} />
                  </>
                )}
              </div>
            )}
          </section>
        )}

        {/* ── PRISMA flow ── */}
        {prismaData && (
          <section>
            <button
              className="flex items-center gap-2 text-sm font-semibold mb-3 hover:text-primary transition-colors"
              onClick={() => setShowPrisma((v) => !v)}
            >
              PRISMA Flow Diagram
              {showPrisma
                ? <ChevronUp className="h-4 w-4" />
                : <ChevronDown className="h-4 w-4" />}
            </button>
            {showPrisma && (
              <div className="space-y-4">
                <PrismaFlowChart data={prismaData} />
                <PrismaBarChart data={prismaData} />
              </div>
            )}
            {!showPrisma && (
              <p className="text-xs text-muted-foreground">
                {prismaData.identified.toLocaleString()} records identified →{" "}
                {prismaData.included.toLocaleString()} included after screening
              </p>
            )}
          </section>
        )}

        {/* ── Studies section ── */}
        <section>
          <h2 className="text-base font-semibold mb-4">Included studies</h2>

          {/* Filter presets */}
          {presetsData?.presets && presetsData.presets.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                <Filter className="h-3 w-3" /> Filter by review
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => { setSelectedPreset(null); setPage(1); }}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    !selectedPreset ? "bg-primary text-primary-foreground border-primary" : "hover:bg-accent"
                  }`}
                >
                  All studies
                </button>
                {presetsData.presets.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => { setSelectedPreset(preset); setPage(1); }}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      selectedPreset?.id === preset.id
                        ? "bg-primary text-primary-foreground border-primary"
                        : "hover:bg-accent"
                    }`}
                    title={preset.description ?? undefined}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search + export */}
          <div className="flex gap-3 mb-4 flex-wrap">
            <input
              type="search"
              placeholder="Search titles, authors…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="flex-1 min-w-48 border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              onClick={() => handleExport("csv")}
              className="flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm hover:bg-accent"
              title="Download all included studies as CSV"
            >
              <Download className="h-4 w-4" /> CSV
            </button>
            <button
              onClick={() => handleExport("bibtex")}
              className="flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm hover:bg-accent"
              title="Download all included studies as BibTeX"
            >
              <Download className="h-4 w-4" /> BibTeX
            </button>
          </div>

          {studiesData && (
            <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1">
              {isFetching && <RefreshCw className="h-3 w-3 animate-spin" />}
              {studiesData.pagination.total.toLocaleString()} studies
              {search && ` matching "${search}"`}
            </p>
          )}

          {/* Table */}
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Study</th>
                  <th className="text-left px-4 py-3 font-medium w-14">Year</th>
                  <th className="text-left px-4 py-3 font-medium">XR Device</th>
                  <th className="text-left px-4 py-3 font-medium w-14">n</th>
                  <th className="text-left px-4 py-3 font-medium w-28">Design</th>
                  <th className="text-left px-4 py-3 font-medium">Reviews</th>
                  <th className="px-4 py-3 w-8" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {isLoading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                      <RefreshCw className="h-5 w-5 animate-spin mx-auto" />
                    </td>
                  </tr>
                )}
                {!isLoading && studiesData?.studies.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                      {search
                        ? `No studies match "${search}"`
                        : "No included studies yet. Check back soon."}
                    </td>
                  </tr>
                )}
                {studiesData?.studies.map((study) => (
                  <tr key={study.id} className="hover:bg-accent/30 transition-colors">
                    <td className="px-4 py-3 max-w-xs">
                      <p className="font-medium leading-snug line-clamp-2">{study.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {study.authors.slice(0, 2).join(", ")}
                        {study.authors.length > 2 ? " et al." : ""}
                        {study.journal ? `, ${study.journal}` : ""}
                      </p>
                      {study.extractionStatus === "preliminary" && (
                        <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800">
                          Preliminary (AI)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{study.year ?? "-"}</td>
                    <td className="px-4 py-3 text-xs">
                      {study.deviceName || study.xrModality ? (
                        <>
                          {study.deviceName && <span>{study.deviceName}</span>}
                          {study.xrModality && (
                            <span className="text-muted-foreground">
                              {study.deviceName ? ` (${formatModality(study.xrModality)})` : formatModality(study.xrModality)}
                            </span>
                          )}
                          {study.deviceManufacturer && (
                            <span className="block text-muted-foreground">{study.deviceManufacturer}</span>
                          )}
                        </>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {study.sampleSize != null ? study.sampleSize : "-"}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {study.studyDesign ? formatDesign(study.studyDesign) : "-"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {study.reviews.length > 0
                          ? study.reviews.map((r) => (
                              <span
                                key={r.id}
                                className="px-1.5 py-0.5 rounded text-[10px] bg-secondary border"
                                title={r.name}
                              >
                                {r.type}
                              </span>
                            ))
                          : <span className="text-muted-foreground text-xs">-</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {study.doi && (
                        <a
                          href={`https://doi.org/${study.doi.replace(/^https?:\/\/doi\.org\//i, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                          title="View paper"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {studiesData && studiesData.pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 border rounded-md text-sm hover:bg-accent disabled:opacity-30"
              >
                Previous
              </button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {studiesData.pagination.totalPages}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page === studiesData.pagination.totalPages}
                className="px-3 py-1.5 border rounded-md text-sm hover:bg-accent disabled:opacity-30"
              >
                Next
              </button>
            </div>
          )}
        </section>
      </div>

      {/* ── Footer ── */}
      <footer className="border-t bg-card mt-12">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div className="max-w-lg">
              <img src="/neuroxrehab-logo.svg" alt="neuroXRehab" className="h-7 w-auto mb-3" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Extended Reality Neurorehabilitation of Spatial Neglect
                and Related Disorders After Brain Injury (neuroXRehab)</strong> is funded by the European
                Cooperation in Science and Technology (COST) as Action CA24123. It brings together
                researchers, clinicians, and technologists from across Europe and beyond to advance XR-based
                assessment and rehabilitation of spatial neglect.
              </p>
            </div>
            <div className="shrink-0 flex flex-col gap-2 text-sm">
              <a
                href={NEUROX_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowUpRight className="h-3.5 w-3.5" />
                neuroxrehab.com
              </a>
              <a
                href={COST_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                COST Action CA24123
              </a>
              {lastUpdated && (
                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                  <RefreshCw className="h-3 w-3" />
                  Database updated {lastUpdated}
                </p>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-6 pt-4 border-t">
            Data is freely available for research and educational purposes.
            Cite individual studies using the BibTeX export above.
            This database is managed by COST Action CA24123 Working Group 1.
          </p>
        </div>
      </footer>
    </div>
  );
}

// ─── Author Word Cloud ────────────────────────────────────────────────────────

function AuthorCloud({
  authors,
  onSearch,
}: {
  authors: { name: string; count: number }[];
  onSearch: (name: string) => void;
}) {
  const [hovered, setHovered] = useState<string | null>(null);

  const { minCount, maxCount } = useMemo(() => ({
    minCount: Math.min(...authors.map((a) => a.count)),
    maxCount: Math.max(...authors.map((a) => a.count)),
  }), [authors]);

  const getStyle = (count: number): React.CSSProperties => {
    const range = maxCount - minCount || 1;
    const t = (count - minCount) / range; // 0–1
    const fontSize = 12 + t * 24;         // 12px–36px
    const opacity = 0.45 + t * 0.55;      // 0.45–1.0
    return { fontSize: `${fontSize}px`, opacity };
  };

  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2 leading-relaxed">
      {authors.map((a) => (
        <button
          key={a.name}
          style={getStyle(a.count)}
          onMouseEnter={() => setHovered(a.name)}
          onMouseLeave={() => setHovered(null)}
          onClick={() => onSearch(a.name.split(" ").pop() ?? a.name)}
          title={`${a.name}: ${a.count} publication${a.count !== 1 ? "s" : ""}`}
          className={`font-medium transition-colors cursor-pointer leading-none ${
            hovered === a.name ? "text-brand-magenta" : "text-foreground"
          }`}
        >
          {a.name}
        </button>
      ))}
    </div>
  );
}

// ─── Stat Pill ────────────────────────────────────────────────────────────────

function StatPill({
  value,
  label,
  highlight,
}: {
  value: number | string;
  label: string;
  highlight?: boolean;
}) {
  return (
    <div className={`rounded-xl p-4 border bg-card ${highlight ? "border-brand-magenta/30 bg-brand-magenta/5" : ""}`}>
      <p className={`text-2xl font-bold tabular-nums ${highlight ? "text-brand-magenta" : ""}`}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

// ─── PRISMA Flow ──────────────────────────────────────────────────────────────

function PrismaFlowChart({ data }: { data: PrismaFlow }) {
  const n = (v: number) => `(n = ${v.toLocaleString()})`;

  const MAIN_X = 60, MAIN_W = 300;
  const SIDE_X = 460, SIDE_W = 300;
  const TAB_X = 10, TAB_W = 34;
  const ARROW_Y_TRIM = 5;

  const rows = [
    { y: 20, h: 110 },
    { y: 180, h: 110 },
    { y: 340, h: 110 },
    { y: 500, h: 100 },
  ];
  const centerOf = (r: (typeof rows)[number]) => r.y + r.h / 2;

  const tabs = [
    { label: "Identification", y: rows[0].y, h: rows[0].h, fill: "fill-primary/10", stroke: "stroke-primary/30", text: "fill-primary" },
    { label: "Screening", y: rows[1].y, h: rows[2].y + rows[2].h - rows[1].y, fill: "fill-muted", stroke: "stroke-border", text: "fill-foreground/70" },
    { label: "Included", y: rows[3].y, h: rows[3].h, fill: "fill-brand-magenta/10", stroke: "stroke-brand-magenta/30", text: "fill-brand-magenta" },
  ];

  const mainBoxes = [
    { row: 0, lines: ["Records identified from", "database searching"], count: n(data.identified) },
    { row: 1, lines: ["Records screened", "(title & abstract)"], count: n(data.screened) },
    { row: 2, lines: ["Reports assessed for", "eligibility (full text)"], count: n(data.fullTextAssessed) },
    { row: 3, lines: ["Studies included", "in review"], count: n(data.included), highlight: true },
  ];

  const sideBoxes = [
    { row: 0, lines: ["Duplicate records", "removed before screening"], count: n(data.duplicatesRemoved) },
    { row: 1, lines: ["Records excluded", "at title & abstract"], count: n(data.excludedAbstract) },
    { row: 2, lines: ["Reports excluded", "at full-text review"], count: n(data.excludedFullText) },
  ];

  const width = 800, height = 620;

  return (
    <div className="border rounded-lg p-5 bg-card">
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${width} ${height}`} style={{ minWidth: 640, maxWidth: 720, margin: "0 auto", display: "block" }} className="w-full h-auto">
          <defs>
            <marker id="prisma-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M0,0 L10,5 L0,10 z" style={{ fill: "hsl(var(--foreground))" }} />
            </marker>
          </defs>

          {/* vertical connectors between main boxes */}
          {rows.slice(0, -1).map((r, i) => {
            const x = MAIN_X + MAIN_W / 2;
            const y1 = r.y + r.h;
            const y2 = rows[i + 1].y - ARROW_Y_TRIM;
            return (
              <line
                key={`v-${i}`}
                x1={x} y1={y1} x2={x} y2={y2}
                className="stroke-foreground/50"
                strokeWidth={1.75}
                markerEnd="url(#prisma-arrow)"
              />
            );
          })}

          {/* horizontal connectors from main to side boxes */}
          {sideBoxes.map((sb, i) => {
            const y = centerOf(rows[sb.row]);
            return (
              <line
                key={`h-${i}`}
                x1={MAIN_X + MAIN_W} y1={y} x2={SIDE_X - ARROW_Y_TRIM} y2={y}
                className="stroke-foreground/50"
                strokeWidth={1.75}
                markerEnd="url(#prisma-arrow)"
              />
            );
          })}

          {/* stage tabs */}
          {tabs.map((t) => (
            <g key={t.label}>
              <rect x={TAB_X} y={t.y} width={TAB_W} height={t.h} rx={5} className={`${t.fill} ${t.stroke}`} strokeWidth={1} />
              <text
                x={TAB_X + TAB_W / 2}
                y={t.y + t.h / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(-90 ${TAB_X + TAB_W / 2} ${t.y + t.h / 2})`}
                className={`${t.text} text-[13px] font-semibold tracking-wide`}
              >
                {t.label}
              </text>
            </g>
          ))}

          {/* main boxes */}
          {mainBoxes.map((b) => {
            const r = rows[b.row];
            const textX = MAIN_X + 16;
            let ty = r.y + 26;
            return (
              <g key={`main-${b.row}`}>
                <rect x={MAIN_X} y={r.y} width={MAIN_W} height={r.h} rx={6} className="fill-card stroke-border" strokeWidth={1.5} />
                {b.lines.map((line, li) => {
                  const el = <text key={li} x={textX} y={ty} className="fill-foreground text-[13px]">{line}</text>;
                  ty += 18;
                  return el;
                })}
                <text
                  x={textX}
                  y={ty + 4}
                  className={`text-[13px] font-bold tabular-nums ${b.highlight ? "fill-brand-magenta" : "fill-foreground"}`}
                >
                  {b.count}
                </text>
              </g>
            );
          })}

          {/* side (removed / excluded) boxes */}
          {sideBoxes.map((b) => {
            const r = rows[b.row];
            const textX = SIDE_X + 16;
            let ty = r.y + 26;
            return (
              <g key={`side-${b.row}`}>
                <rect x={SIDE_X} y={r.y} width={SIDE_W} height={r.h} rx={6} className="fill-card stroke-border" strokeWidth={1.5} />
                {b.lines.map((line, li) => {
                  const el = <text key={li} x={textX} y={ty} className="fill-muted-foreground text-[13px]">{line}</text>;
                  ty += 18;
                  return el;
                })}
                <text x={textX} y={ty + 4} className="text-[13px] font-bold tabular-nums fill-destructive/80">
                  {b.count}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      <p className="text-[11px] text-muted-foreground mt-3 text-center">
        Adapted from the PRISMA 2020 flow diagram (Page et al., BMJ 2021;372:n71). See{" "}
        <a href="http://www.prisma-statement.org/" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">
          prisma-statement.org
        </a>.
      </p>
    </div>
  );
}

function PrismaBarChart({ data }: { data: PrismaFlow }) {
  const chartData = [
    { label: "Identified", value: data.identified, color: "#f0c070" },
    { label: "After dedup", value: data.identified - data.duplicatesRemoved, color: "#f0a030" },
    { label: "Title/abstract", value: data.screened, color: "#ed7800" },
    { label: "Full-text", value: data.fullTextAssessed, color: "#c06000" },
    { label: "Included", value: data.included, color: "#22c55e" },
  ];

  return (
    <div className="border rounded-lg p-5 bg-card">
      <ResponsiveContainer width="100%" height={190}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 110, right: 50 }}>
          <XAxis type="number" tick={{ fontSize: 11 }} />
          <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={100} />
          <Tooltip formatter={(v: number) => [v.toLocaleString(), "Studies"]} />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {chartData.map((entry, i) => (
              <Cell key={i} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="grid grid-cols-3 gap-4 mt-2 text-xs text-muted-foreground text-center border-t pt-4">
        <div>
          <p className="font-bold text-lg text-foreground">{data.duplicatesRemoved.toLocaleString()}</p>
          <p>Duplicates removed</p>
        </div>
        <div>
          <p className="font-bold text-lg text-foreground">
            {(data.excludedAbstract + data.excludedFullText).toLocaleString()}
          </p>
          <p>Total excluded</p>
        </div>
        <div>
          <p className="font-bold text-lg text-brand-magenta">{data.included.toLocaleString()}</p>
          <p>Included</p>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MODALITY_LABELS: Record<string, string> = {
  HMD_VR: "HMD-VR", HMD_MR: "HMD-MR", MOBILE_AR: "Mobile AR", OTHER: "Other XR", NOT_SPECIFIED: "XR",
};
function formatModality(m: string) { return MODALITY_LABELS[m] ?? m; }

const DESIGN_LABELS: Record<string, string> = {
  RCT: "RCT", QUASI_EXPERIMENTAL: "Quasi-exp.", CASE_SERIES: "Case series",
  SINGLE_CASE: "Single case", TECHNICAL: "Technical", OTHER: "Other",
};
function formatDesign(d: string) { return DESIGN_LABELS[d] ?? d; }
